import React, { type ReactElement, type ReactNode } from "react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ndd/ui";
import type { RdScheduleActionScope } from "../../../../../shared/rd-schedule-actions";
import {
  shortDateLabel,
  timeLabel,
  type ActionProblem,
} from "../../../lib/rd-schedule-actions";
import type { TimePick } from "./use-time-pick";

// NO-876. The parts the five appointment-action dialogs share. Presentational
// only: no router, auth or query client, so `tsx --test` renders them.

// Local on purpose: the shared Field gives its hint no id, and these dialogs need
// one for aria-describedby (apps/design-system/AGENTS.md, Usage Contract).
export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? (
        <p id={`${id}-hint`} className="text-body text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function ActionError({
  problem,
}: {
  problem: ActionProblem | null;
}): ReactElement | null {
  if (!problem) return null;

  return (
    <div
      role="alert"
      className="rounded-lg bg-danger-soft px-3 py-2 text-body text-danger"
    >
      <p className="font-semibold">{problem.message}</p>
      <p>{problem.fix}</p>
      {problem.dates.length > 0 ? (
        <p>Not open: {problem.dates.map((date) => shortDateLabel(date)).join(", ")}.</p>
      ) : null}
    </div>
  );
}

export function EmailCheckbox({
  id,
  label,
  checked,
  disabled,
  note,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  note?: string;
  onChange: (checked: boolean) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2.5">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          aria-describedby={note ? `${id}-note` : undefined}
          onCheckedChange={(value) => onChange(value === true)}
        />
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
      </div>
      {note ? (
        <p id={`${id}-note`} className="pl-7 text-body text-muted-foreground">
          {note}
        </p>
      ) : null}
    </div>
  );
}

function ScopeOption({
  id,
  value,
  label,
  hint,
  disabled,
}: {
  id: string;
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="flex items-start gap-2.5">
      <RadioGroupItem id={id} value={value} disabled={disabled} className="mt-0.5" />
      <Label htmlFor={id} className="flex flex-col gap-0.5 font-normal">
        <span>{label}</span>
        {hint ? <span className="text-body text-muted-foreground">{hint}</span> : null}
      </Label>
    </div>
  );
}

export interface ScopeFieldsProps {
  idPrefix: string;
  legend: string;
  value: RdScheduleActionScope;
  onChange: (scope: RdScheduleActionScope) => void;
  disabled?: boolean;
  thisLabel: string;
  thisHint?: string;
  laterLabel: string;
  laterHint?: string;
  /** Set when "this and all later" is refused (a series that is too long). */
  laterDisabledNote?: string;
}

export function ScopeFields(props: ScopeFieldsProps): ReactElement {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-body-lg font-semibold">{props.legend}</legend>
      <RadioGroup
        value={props.value}
        disabled={props.disabled}
        onValueChange={(next) => props.onChange(next as RdScheduleActionScope)}
        aria-label={props.legend}
      >
        <ScopeOption
          id={`${props.idPrefix}-this`}
          value="this"
          label={props.thisLabel}
          hint={props.thisHint}
        />
        <ScopeOption
          id={`${props.idPrefix}-later`}
          value="this_and_later"
          label={props.laterLabel}
          hint={props.laterDisabledNote ?? props.laterHint}
          disabled={Boolean(props.laterDisabledNote)}
        />
      </RadioGroup>
    </fieldset>
  );
}

export function TypeSelect({
  id,
  hasHint,
  value,
  types,
  disabled,
  onChange,
}: {
  id: string;
  hasHint: boolean;
  value: string | null;
  types: { id: string; name: string; durationMinutes: number }[];
  disabled?: boolean;
  onChange: (typeId: string) => void;
}): ReactElement {
  return (
    <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} aria-describedby={hasHint ? `${id}-hint` : undefined}>
        <SelectValue placeholder="Choose a type" />
      </SelectTrigger>
      <SelectContent>
        {types.map((type) => (
          <SelectItem key={type.id} value={type.id}>
            {`${type.name}, ${type.durationMinutes} min`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function openTimesNote(
  pick: TimePick,
  date: string,
  durationMinutes: number | null
): string {
  if (pick.status === "loading") return "Looking for open times…";

  if (pick.status === "error") return "Open times did not load.";

  if (!pick.hoursSet) return "You have not set your hours yet.";

  const length = durationMinutes ? ` Each is ${durationMinutes} minutes.` : "";

  return `From your hours on ${shortDateLabel(date)}.${length}`;
}

function SlotButtons({
  pick,
  labelId,
  disabled,
}: {
  pick: TimePick;
  labelId: string;
  disabled?: boolean;
}): ReactElement {
  if (pick.slots.length === 0) {
    return (
      <p className="text-body text-muted-foreground">
        No open times that day. Pick another date, or use a time outside your hours.
      </p>
    );
  }

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="grid grid-cols-2 gap-2 sm:grid-cols-4"
    >
      {pick.slots.map((slot) => (
        <Button
          key={slot.startsAt}
          type="button"
          variant="utility"
          aria-pressed={pick.selected === slot.startsAt}
          disabled={disabled}
          className="font-mono aria-pressed:bg-primary-soft aria-pressed:font-medium"
          onClick={() => pick.select(slot.startsAt)}
        >
          {timeLabel(slot.localTime)}
        </Button>
      ))}
    </div>
  );
}

function OutsideTime({
  idPrefix,
  pick,
  disabled,
}: {
  idPrefix: string;
  pick: TimePick;
  disabled?: boolean;
}): ReactElement {
  return (
    <Field
      id={`${idPrefix}-outside`}
      label="Time outside my hours"
      hint="This skips the check against your hours. Your other visits and blocked time are still checked."
    >
      <Input
        id={`${idPrefix}-outside`}
        type="time"
        step={300}
        value={pick.outsideTime}
        disabled={disabled}
        aria-describedby={`${idPrefix}-outside-hint`}
        onChange={(event) => pick.setOutsideTime(event.target.value)}
      />
    </Field>
  );
}

function OpenTimes({
  idPrefix,
  date,
  pick,
  durationMinutes,
  disabled,
}: {
  idPrefix: string;
  date: string;
  pick: TimePick;
  durationMinutes: number | null;
  disabled?: boolean;
}): ReactElement {
  const labelId = `${idPrefix}-times`;

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-body-lg font-semibold">
        Open times
      </span>
      <p className="-mt-1 text-body text-muted-foreground" aria-live="polite">
        {openTimesNote(pick, date, durationMinutes)}
      </p>
      {pick.status === "error" ? (
        <Button
          type="button"
          variant="utility"
          size="sm"
          className="self-start"
          onClick={pick.reload}
        >
          Try again
        </Button>
      ) : null}
      {pick.status === "ready" && !pick.outside ? (
        <SlotButtons pick={pick} labelId={labelId} disabled={disabled} />
      ) : null}
      {pick.outside ? (
        <OutsideTime idPrefix={idPrefix} pick={pick} disabled={disabled} />
      ) : null}
      <Button
        type="button"
        variant="quiet-link"
        size="default"
        className="h-auto self-start p-0"
        aria-pressed={pick.outside}
        disabled={disabled}
        onClick={() => pick.setOutside(!pick.outside)}
      >
        {pick.outside ? "Pick from my open times" : "Use a time outside my hours"}
      </Button>
    </div>
  );
}

/** The date, the open times for it, and the escape hatch for a time outside the RD's hours. */
export function TimePicker({
  idPrefix,
  dateLabel,
  pick,
  durationMinutes,
  disabled,
  onDateChange,
  date,
  minDate,
}: {
  idPrefix: string;
  dateLabel: string;
  pick: TimePick;
  durationMinutes: number | null;
  disabled?: boolean;
  date: string;
  minDate?: string;
  onDateChange: (date: string) => void;
}): ReactElement {
  return (
    <>
      <Field id={`${idPrefix}-date`} label={dateLabel}>
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={date}
          min={minDate}
          disabled={disabled}
          onChange={(event) => onDateChange(event.target.value)}
        />
      </Field>
      <OpenTimes
        idPrefix={idPrefix}
        date={date}
        pick={pick}
        durationMinutes={durationMinutes}
        disabled={disabled}
      />
    </>
  );
}
